import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Users2, ClipboardCheck, Plane } from "lucide-react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RT } from "recharts";
import ChartInsightButton from "@/components/insights/ChartInsightButton";

export default function HRPage() {
  const [emps, setEmps] = useState([]);
  const [att, setAtt] = useState([]);
  const [leaves, setLeaves] = useState([]);

  useEffect(() => {
    api.get("/erp/hr/employees").then((r) => setEmps(r.data.items));
    api.get("/erp/hr/attendance").then((r) => setAtt(r.data.items));
    api.get("/erp/hr/leave-requests").then((r) => setLeaves(r.data.items));
  }, []);

  return (
    <div className="space-y-6">
      <div>
        <div className="label-tiny text-[hsl(var(--primary))]">HRMS</div>
        <h1 className="mt-1 text-3xl font-heading font-bold">Human Resources</h1>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPI icon={Users2} label="Employees" value={emps.length}/>
        <KPI icon={ClipboardCheck} label="Present today" value={att[0]?.present || "-"}/>
        <KPI icon={Plane} label="Pending Leaves" value={leaves.filter(l=>l.status === "pending").length}/>
        <KPI icon={Users2} label="Departments" value={new Set(emps.map(e=>e.department)).size}/>
      </div>

      <div className="rounded-2xl border border-border bg-card p-5 relative">
        <div className="flex items-start justify-between gap-2">
          <div>
            <div className="label-tiny">Attendance Trend</div>
            <div className="font-heading font-semibold">Last 30 days</div>
          </div>
          <ChartInsightButton title="HR — Attendance Trend (Last 30 days)" data={att.slice(0, 30)} />
        </div>
        <div className="h-56 mt-4">
          <ResponsiveContainer>
            <LineChart data={att.slice().reverse()}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))"/>
              <XAxis dataKey="date" fontSize={10}/>
              <YAxis fontSize={12}/>
              <RT contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 8 }}/>
              <Line type="monotone" dataKey="present" stroke="#00A859" strokeWidth={2} dot={false}/>
              <Line type="monotone" dataKey="on_leave" stroke="#FF9933" strokeWidth={2} dot={false}/>
              <Line type="monotone" dataKey="absent" stroke="#EF4444" strokeWidth={2} dot={false}/>
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="rounded-2xl border border-border bg-card p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="font-heading font-semibold">Employees</div>
            <div className="text-xs text-muted-foreground">Showing 8 of {emps.length}</div>
          </div>
          <div className="space-y-2">
            {emps.slice(0, 8).map((e) => (
              <div key={e.id} className="flex items-center gap-3 p-2 rounded-md hover:bg-muted/40">
                <div className="h-9 w-9 rounded-full bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))] grid place-items-center font-semibold">{e.name.split(" ").map(w=>w[0]).join("")}</div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{e.name}</div>
                  <div className="text-xs text-muted-foreground">{e.designation} · {e.department}</div>
                </div>
                <div className="text-xs font-mono">{e.emp_code}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="rounded-2xl border border-border bg-card p-5">
          <div className="font-heading font-semibold mb-3">Leave Requests</div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr>
                  <th className="table-th">Emp</th><th className="table-th">Type</th><th className="table-th">From</th><th className="table-th">To</th><th className="table-th">Status</th>
                </tr>
              </thead>
              <tbody>
                {leaves.slice(0, 10).map(l => (
                  <tr key={l.id} className="border-t border-border">
                    <td className="table-cell font-mono">{l.employee}</td>
                    <td className="table-cell">{l.type}</td>
                    <td className="table-cell text-muted-foreground">{l.from_date}</td>
                    <td className="table-cell text-muted-foreground">{l.to_date}</td>
                    <td className="table-cell"><span className={`stat-pill capitalize ${l.status === "approved" ? "bg-emerald-500/10 text-emerald-500" : l.status === "rejected" ? "bg-red-500/10 text-red-500" : "bg-amber-500/10 text-amber-500"}`}>{l.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

function KPI({ icon: Icon, label, value }) {
  return (
    <div className="kpi-card">
      <div className="h-10 w-10 rounded-lg bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))] grid place-items-center"><Icon className="h-5 w-5"/></div>
      <div className="mt-3 font-heading text-2xl font-bold">{value}</div>
      <div className="text-xs text-muted-foreground mt-1">{label}</div>
    </div>
  );
}
